import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandM, BrandS } from '@sorare/core/src/atoms/typography';
import { Season26TutorialDialog } from '@sorare/core/src/components/dialog/Season26TutorialDialog';
import { Season26TutorialStep } from '@sorare/core/src/components/dialog/Season26TutorialStep';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';
import { categoryLabels, statLabels } from '@sorare/core/src/lib/scoring';

import { QuickPlayTutorialFooter } from '#src/components/quickPlay/QuickPlayTutorialDialog/QuickPlayTutorialFooter/index.tsx';
import { TutorialBezel } from '#src/components/quickPlay/QuickPlayTutorialDialog/TutorialBezel/index.tsx';
import type { QuickPlayTutorialDialogType } from '#src/components/quickPlay/QuickPlayTutorialDialog/types.ts';
import { QuickPlayTutorialHeader } from '#src/components/quickPlay/QuickPlayTutorialHeader/index.tsx';

import { Actions } from './Actions/index.tsx';
import { Milestones } from './Milestones/index.tsx';
import { NegativeActions } from './NegativeActions/index.tsx';
import background from './assets/background.webp';

type Props = {
  open: boolean;
  onContinue: () => void;
  loading: boolean;
  onSelect: (type: QuickPlayTutorialDialogType) => void;
};

const ICON_SIZE = 2;

const footballItems = [
  {
    key: 'all-around-actions',
    color: 'var(--c-score-high)',
    label: (
      <FormattedMessage
        id="QuickplayTutorialDialog.scoring.allAroundActions"
        defaultMessage="ALL AROUND ACTIONS"
      />
    ),
    title: (
      <FormattedMessage
        id="QuickplayTutorialDialog.scoring.over45PlusStats"
        defaultMessage="OVER 45+ STATS"
      />
    ),
    subtitle: (
      <FormattedMessage
        id="QuickplayTutorialDialog.scoring.effectYourOverallScore"
        defaultMessage="effect your overall score"
      />
    ),
    stats: [
      {
        label: <FormattedMessage {...categoryLabels.GENERAL} />,
        icon: <StatIcon statKey="goals" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...categoryLabels.ATTACKING} />,
        icon: <StatIcon statKey="assist_penalty_won" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...categoryLabels.PASSING} />,
        icon: <StatIcon statKey="goal_assist" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...categoryLabels.POSSESSION} />,
        icon: <StatIcon statKey="last_man_tackle" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...categoryLabels.DEFENDING} />,
        icon: <StatIcon statKey="clearance_off_line" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...categoryLabels.GOALKEEPING} />,
        icon: <StatIcon statKey="clean_sheet_60" size={ICON_SIZE} />,
      },
    ],
  },
  {
    key: 'decisive-actions',
    color: 'var(--c-green-400)',
    label: (
      <FormattedMessage
        id="QuickplayTutorialDialog.scoring.decisiveActions"
        defaultMessage="DECISIVE ACTIONS"
      />
    ),
    title: (
      <FormattedMessage
        id="QuickplayTutorialDialog.scoring.jumpTo60PlusPts"
        defaultMessage="JUMP TO 60+ PTS"
      />
    ),
    subtitle: (
      <FormattedMessage
        id="QuickplayTutorialDialog.scoring.consecutiveActions"
        defaultMessage="+10 for every consecutive action"
      />
    ),
    stats: [
      {
        label: <FormattedMessage {...statLabels.goals} />,
        icon: <StatIcon statKey="goals" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...categoryLabels.ATTACKING} />,
        icon: <StatIcon statKey="goals" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...statLabels.assists} />,
        icon: <StatIcon statKey="goal_assist" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...statLabels.last_man_tackle} />,
        icon: <StatIcon statKey="last_man_tackle" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...statLabels.penalty_won} />,
        icon: <StatIcon statKey="assist_penalty_won" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...statLabels.penalty_save} />,
        icon: <StatIcon statKey="penalty_save" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...statLabels.clearance_off_line} />,
        icon: <StatIcon statKey="clearance_off_line" size={ICON_SIZE} />,
      },
    ],
  },
  {
    key: 'decisive-actions',
    color: 'var(--c-red-400)',
    label: (
      <FormattedMessage
        id="QuickplayTutorialDialog.scoring.negativeActions2"
        defaultMessage="NEGATIVE ACTIONS"
      />
    ),
    title: (
      <FormattedMessage
        id="QuickplayTutorialDialog.scoring.dropTo30MinusPts"
        defaultMessage="DROP To 30- PTS"
      />
    ),
    subtitle: (
      <FormattedMessage
        id="QuickplayTutorialDialog.scoring.consecutiveNegativeActions"
        defaultMessage="-10 for every consecutive action"
      />
    ),
    stats: [
      {
        label: <FormattedMessage {...statLabels.red_card} />,
        icon: <StatIcon statKey="red_card" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...statLabels.error_lead_to_goal} />,
        icon: <StatIcon statKey="error_lead_to_goal" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...statLabels.own_goals} />,
        icon: <StatIcon statKey="own_goals" size={ICON_SIZE} />,
      },
      {
        label: <FormattedMessage {...statLabels.penalty_conceded} />,
        icon: <StatIcon statKey="penalty_conceded" size={ICON_SIZE} />,
      },
    ],
  },
];

const List = styled(Horizontal)`
  flex-wrap: wrap;
  row-gap: var(--triple-unit);
  & > * {
    flex-basis: 45%;
  }
`;

export const ScoringDialog = ({
  open,
  onContinue,
  loading,
  onSelect,
}: Props) => {
  const { sport } = useSportContext();
  return (
    <Season26TutorialDialog
      open={open}
      onClose={onContinue}
      color="#78C2FF"
      textColor="var(--c-black)"
      headerBackgroundUrl={background}
      disableAnimation
      hideTextureBackground
      gap={2}
      titleGroup={
        <QuickPlayTutorialHeader
          overline={<FormattedMessage {...glossary.howToPlay} />}
          title={
            <FormattedMessage
              id="ScoringDialog.title"
              defaultMessage="Scoring"
            />
          }
        />
      }
      items={
        sport === Sport.FOOTBALL
          ? footballItems.map(item => (
              <Season26TutorialStep
                stepTitle={item.label}
                fullHeight={false}
                key={item.key}
              >
                <TutorialBezel color={item.color}>
                  <Vertical gap={3} center>
                    <Vertical className="text-center">
                      <BrandM>{item.title}</BrandM>
                      <BodyM color="var(--c-nd-600)">{item.subtitle}</BodyM>
                    </Vertical>
                    <List gap={2}>
                      {item.stats.map((stat, index: number) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Horizontal key={item.key + index}>
                          {stat.icon}
                          <BrandS color="var(--c-nd-600)">{stat.label}</BrandS>
                        </Horizontal>
                      ))}
                    </List>
                  </Vertical>
                </TutorialBezel>
              </Season26TutorialStep>
            ))
          : [
              <Season26TutorialStep
                stepTitle={
                  <FormattedMessage
                    id="QuickplayTutorialDialog.scoring.actions"
                    defaultMessage="Actions"
                  />
                }
                key="l10-average"
                fullHeight={false}
              >
                <Actions />
              </Season26TutorialStep>,
              <Season26TutorialStep
                stepTitle={
                  <FormattedMessage
                    id="QuickplayTutorialDialog.scoring.milestones"
                    defaultMessage="Milestones"
                  />
                }
                key="targets"
                fullHeight={false}
              >
                <Milestones />
              </Season26TutorialStep>,
              <Season26TutorialStep
                stepTitle={
                  <FormattedMessage
                    id="QuickplayTutorialDialog.scoring.negativeActions"
                    defaultMessage="Negative Actions"
                  />
                }
                key="success"
                fullHeight={false}
              >
                <NegativeActions />
              </Season26TutorialStep>,
            ]
      }
      footer={
        <QuickPlayTutorialFooter
          onSelect={onSelect}
          onContinue={onContinue}
          loading={loading}
          selectedType="scoring"
        />
      }
    />
  );
};
